/**
 * "Current Position Filters" — server-side filters over the contact's own
 * promoted position (company first, then position), as opposed to the BCT
 * section's filters over a match reason.
 *
 * Rendered on EVERY import detail page regardless of format: both inputs read
 * denormalized `import_contacts` columns that every import format populates, so
 * unlike `bct_*` the API never gates them. On a BCT import this section pairs
 * with "Company Map Filters" — general filter first, format-specific one
 * after it, the two separated by that group's left rule. They share one line
 * only when the content area is wide enough, and wrap onto two lines below
 * that — `ContactsTable`'s layout note holds the measured threshold and is the
 * single source for it.
 *
 * Fully controlled and presentational: the state, the 300 ms debounce and the
 * page-1 reset all live in `ImportDetailPage`, the same way the BCT inputs
 * work. Nothing here knows about the query string.
 */
import { filterInputClass } from "./filterInputClass";

export function ContactFilterBar({
  currentCompanyFilter,
  onCurrentCompanyFilterChange,
  currentPositionFilter,
  onCurrentPositionFilterChange,
}: {
  currentCompanyFilter: string;
  onCurrentCompanyFilterChange: (value: string) => void;
  currentPositionFilter: string;
  onCurrentPositionFilterChange: (value: string) => void;
}) {
  return (
    <fieldset
      data-testid="contact-filters"
      className="flex flex-row flex-wrap items-center gap-3"
    >
      {/* `fieldset` + `sr-only` `legend` is the pattern the sibling filter bars
          in this same header already use (`ReviewStatusFilterBar`,
          `EnrichmentFilterBar`). It is what conveys the grouping to assistive
          tech now that the two filter sections share a line — layout position
          used to do that job. The legend carries the accessible name, so the
          visible span is `aria-hidden` to avoid announcing the same copy
          twice; each input keeps its own `aria-label`.

          DO NOT "simplify" this into a single VISIBLE `legend`: a rendered
          legend is pulled out of its fieldset's flex formatting context, so
          the label jumps above the inputs and the row grows from 79px to
          ~113px at 1280 (measured). `sr-only` legends are layout-inert
          precisely because they are clipped to a 1x1 box — that is the whole
          reason the copy is written twice here. */}
      <legend className="sr-only">Current Position Filters</legend>
      <span aria-hidden="true" className="text-label text-text-secondary">
        Current Position Filters:
      </span>
      <input
        type="text"
        data-testid="current-company-filter"
        aria-label="Filter by current company"
        placeholder="Current Company…"
        value={currentCompanyFilter}
        onChange={(e) => onCurrentCompanyFilterChange(e.target.value)}
        className={filterInputClass}
      />
      <input
        type="text"
        data-testid="current-position-filter"
        aria-label="Filter by current position"
        placeholder="Current Position…"
        value={currentPositionFilter}
        onChange={(e) => onCurrentPositionFilterChange(e.target.value)}
        className={filterInputClass}
      />
    </fieldset>
  );
}
